/**
 * useEditLock — pessimistic edit lock for shared editable resources.
 *
 * Mount this hook with (resourceType, resourceId) and it will:
 *   1. Generate a per-tab session UUID and try to acquire the lock.
 *   2. If acquired, heartbeat every 2 minutes to keep the 10-minute TTL fresh.
 *   3. If denied, poll status every 10 seconds and flip to "held" when the
 *      previous editor releases or their lock expires.
 *   4. If we held the lock and it was lost (TTL expired while user was idle,
 *      or another user clicked "Take over"), surface `lostLock = true` so the
 *      parent can show a recovery modal — we deliberately do NOT auto-re-acquire
 *      because the form data is now stale relative to whatever the next editor
 *      saved.
 *   5. Release the lock on unmount (best-effort fetch — only fires on clean
 *      React unmount such as route changes or save. Tab close / browser kill
 *      do NOT reliably fire this; the 10-minute TTL is the safety net for
 *      those cases).
 *
 * Strict Mode / fast remounts: cancellation is scoped to each effect run
 * via a local `cancelled` flag, NOT a component-scoped ref. This prevents
 * orphaned timers from a torn-down effect mutating state belonging to a
 * fresh effect run.
 *
 * Same user, two tabs: the backend auto-takes over for the same user across
 * sessions, so the second tab acquires immediately. Cross-user takeover
 * requires `takeover()` (the "Take over editing" button after social confirmation).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../apiHelpers';

export type EditLockStatus = 'idle' | 'acquiring' | 'held' | 'denied' | 'error';

export interface LockHolder {
  user_id: string;
  user_name: string;
  acquired_at: string;
  expires_at: string;
}

export interface UseEditLockResult {
  status: EditLockStatus;
  holder: LockHolder | null;
  /** Per-tab session UUID. Send as X-Edit-Lock-Session on save calls. */
  sessionId: string;
  /** True after we held the lock and lost it (TTL expired or another user took over). Parent should warn the user. */
  lostLock: boolean;
  error: string | null;
  retry: () => void;
  /** Force takeover from the current holder. Caller is responsible for confirming with the user first. */
  takeover: () => void;
  /** Fire an immediate heartbeat. Used by the "Still editing?" idle nudge. */
  ping: () => Promise<boolean>;
  /** Clear the lostLock flag after the user has acknowledged it. */
  acknowledgeLostLock: () => void;
  /** Imperative trigger when the parent observes a server-side 409 / lost-lock signal. */
  signalLockLost: () => void;
}

const HEARTBEAT_INTERVAL_MS = 120_000;   // 2 min — well under the 10 min TTL
const DENIED_POLL_INTERVAL_MS = 10_000;  // 10 sec — banner refresh cadence while waiting

function generateSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for older browsers — not cryptographically strong, but session IDs
  // are scoped to a single tab's lifetime and are paired with user_id on the
  // backend so a leaked session_id alone isn't enough to act on a lock.
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function useEditLock(
  resourceType: string,
  resourceId: string | number | null | undefined,
  enabled: boolean = true,
): UseEditLockResult {
  const api = useApi();
  const sessionIdRef = useRef<string>(generateSessionId());

  const [status, setStatus] = useState<EditLockStatus>('idle');
  const [holder, setHolder] = useState<LockHolder | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lostLock, setLostLock] = useState<boolean>(false);
  const [retryNonce, setRetryNonce] = useState<number>(0);
  // One-shot flag — set by takeover(), consumed and cleared at the start of
  // the next effect run. Survives the React state batch so the effect re-run
  // triggered by retryNonce sees it.
  const overrideOnNextAcquireRef = useRef<boolean>(false);

  // Latest path/sessionId pinned outside the effect so `ping` and
  // `acknowledgeLostLock` can use them without re-creating per render.
  const idStr = resourceId != null ? String(resourceId) : '';
  const path = `/edit-locks/${encodeURIComponent(resourceType)}/${encodeURIComponent(idStr)}`;
  const pathRef = useRef(path);
  pathRef.current = path;

  const retry = useCallback(() => {
    setRetryNonce((n) => n + 1);
  }, []);

  const takeover = useCallback(() => {
    overrideOnNextAcquireRef.current = true;
    setRetryNonce((n) => n + 1);
  }, []);

  const acknowledgeLostLock = useCallback(() => {
    setLostLock(false);
  }, []);

  const signalLockLost = useCallback(() => {
    // Called by parent when a server response (e.g. 409 from save) indicates
    // the lock is gone. Don't gate on `everHeld` here — if the user got far
    // enough to attempt a save, they had the lock at some point.
    setLostLock(true);
    setStatus('denied');
  }, []);

  // One-shot heartbeat callable from the outside (used by "still editing?" nudge).
  // Returns true if the lock is still held after the ping.
  const ping = useCallback(async (): Promise<boolean> => {
    try {
      const data = await api.post(`${pathRef.current}/heartbeat`, {
        body: { sessionId: sessionIdRef.current },
        silent: true,
      });
      return !!(data && data.ok);
    } catch {
      return false;
    }
    // api.post identity churns each render; intentionally not in deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!enabled || !idStr) {
      setStatus('idle');
      setHolder(null);
      return;
    }

    // Per-effect cancellation flag. A torn-down effect can never write state
    // belonging to a fresh effect run because each captures its own `cancelled`.
    let cancelled = false;
    let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let everHeld = false;  // true once acquire succeeded — used to flag lostLock vs. initial denial

    // Consume the one-shot override flag now so subsequent effect re-runs
    // (e.g. resourceId change, retry()) don't accidentally inherit it.
    const overrideOnInitialAcquire = overrideOnNextAcquireRef.current;
    overrideOnNextAcquireRef.current = false;

    const sessionId = sessionIdRef.current;
    setStatus('acquiring');
    setError(null);
    // Override implies user is intentionally re-engaging; clear any stale
    // lost-lock state from a previous holding session.
    if (overrideOnInitialAcquire) setLostLock(false);

    const clearTimers = () => {
      if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null; }
      if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    };

    const tryAcquire = async (override = false) => {
      try {
        const data = await api.post(`${path}/acquire`, {
          body: override ? { sessionId, override: true } : { sessionId },
          silent: true,
        });
        if (cancelled) return;

        if (data?.acquired) {
          everHeld = true;
          setStatus('held');
          setHolder(null);
          heartbeatTimer = setInterval(() => { if (!cancelled) heartbeat(); }, HEARTBEAT_INTERVAL_MS);
        } else {
          setStatus('denied');
          setHolder(data?.holder || null);
          pollTimer = setInterval(() => { if (!cancelled) pollForRelease(); }, DENIED_POLL_INTERVAL_MS);
        }
      } catch (err: any) {
        if (cancelled) return;
        setStatus('error');
        setError(err?.message || 'Could not contact lock server');
      }
    };

    const heartbeat = async () => {
      try {
        const data = await api.post(`${path}/heartbeat`, {
          body: { sessionId },
          silent: true,
        });
        if (cancelled) return;
        if (data && data.ok === false) {
          // Lost the lock. Stop heartbeating, surface lostLock so the parent
          // can warn. Do NOT auto-re-acquire — form data is potentially stale.
          clearTimers();
          if (everHeld) setLostLock(true);
          setStatus('denied');
          // Re-poll so the banner correctly reflects who has it now.
          pollForRelease();
          pollTimer = setInterval(() => { if (!cancelled) pollForRelease(); }, DENIED_POLL_INTERVAL_MS);
        }
      } catch {
        // network blip — next heartbeat will catch up
      }
    };

    const pollForRelease = async () => {
      try {
        const data = await api.get(`${path}`, { silent: true });
        if (cancelled) return;
        if (!data?.lock) {
          // Free again. If we never held, attempt to acquire. If we did hold
          // and lost it, do NOT auto-acquire — wait for user to acknowledge.
          if (!everHeld && !lostLock) {
            clearTimers();
            tryAcquire();
          } else {
            setHolder(null);
          }
        } else {
          setHolder(data.lock);
        }
      } catch {
        // ignore transient errors during polling
      }
    };

    tryAcquire(overrideOnInitialAcquire);

    return () => {
      cancelled = true;
      clearTimers();
      // Best-effort release. Only fires on clean React unmount (route change,
      // save, parent re-render that drops this component). Tab close and
      // browser kill do NOT execute this — TTL handles those.
      api.post(`${path}/release`, {
        body: { sessionId },
        silent: true,
      }).catch(() => { /* ignore */ });
    };
    // `api` is intentionally excluded — useApi() returns a fresh object each render
    // and including it would re-run the effect on every parent render (infinite loops
    // and lock re-acquires). The closure over the latest `api` is fine because
    // mutating fetches just need to send the same JWT, which is held by `api`'s logout
    // ref, not its identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceType, idStr, enabled, retryNonce]);

  return {
    status,
    holder,
    sessionId: sessionIdRef.current,
    lostLock,
    error,
    retry,
    takeover,
    ping,
    acknowledgeLostLock,
    signalLockLost,
  };
}
